// @ts-nocheck
/**
 * ファンド価格エンジン（v3.0 リファクタリング版）
 *
 * 【設計変更】
 * - ファンドは独自の価格を持たない
 * - ファンド価格は常にカード価格から計算される（fundCalculator.js）
 * - このファイルは価格履歴の記録と後方互換性のためのラッパー
 *
 * 設計: market_economy_system_v3.md
 */

import { INVESTMENT } from '../../data/constants';
import { ALL_FUNDS } from '../data/funds';
import {
  getFundIndex as calcFundIndex,
  getFundPrice as calcFundPrice,
  getFundChangeRate,
  getAllFundIndices,
  clearCacheIfNeeded,
} from '../../market/fundCalculator';

// ========================================
// 価格取得（fundCalculatorへのデリゲート）
// ========================================

/**
 * ファンドの現在価格を取得（カード価格から計算）
 *
 * 新シグネチャ: getFundPrice(fundId, marketIndex, marketState)
 * 旧シグネチャ（後方互換）: getFundPrice(investmentData, fundId)
 *
 * @param {string|Object} fundIdOrInvestmentData - ファンドIDまたは投資データ（後方互換）
 * @param {Object|string} marketIndexOrFundId - マーケットインデックスまたはファンドID（後方互換）
 * @param {Object} marketState - 市場状態（新シグネチャのみ）
 * @returns {number} 現在価格
 */
export function getFundPrice(fundIdOrInvestmentData, marketIndexOrFundId, marketState) {
  // 後方互換性: 第1引数がオブジェクト（investmentData）の場合
  if (typeof fundIdOrInvestmentData === 'object' && fundIdOrInvestmentData !== null) {
    const investmentData = fundIdOrInvestmentData;
    const fundId = marketIndexOrFundId;
    // 価格履歴の最新値を使用
    const history = investmentData.priceHistory?.[fundId];
    if (history && history.length > 0) {
      return history[history.length - 1];
    }
    // fundPrices（一時的に残している）を使用
    if (investmentData.fundPrices?.[fundId]) {
      return investmentData.fundPrices[fundId];
    }
    // フォールバック: 基準価格
    const fund = ALL_FUNDS[fundId];
    return fund ? fund.basePrice : INVESTMENT.BASE_PRICE;
  }

  // 新シグネチャ: getFundPrice(fundId, marketIndex, marketState)
  const fundId = fundIdOrInvestmentData;
  const marketIndex = marketIndexOrFundId;

  if (!marketIndex || !marketState) {
    // フォールバック: 基準価格を返す
    const fund = ALL_FUNDS[fundId];
    return fund ? fund.basePrice : INVESTMENT.BASE_PRICE;
  }
  return calcFundPrice(fundId, marketIndex, marketState);
}

/**
 * ファンドの指数を取得（カード価格から計算）
 *
 * @param {string} fundId - ファンドID
 * @param {Object} marketIndex - マーケットインデックス
 * @param {Object} marketState - 市場状態
 * @returns {number} 指数（100 = 基準値）
 */
export function getFundIndex(fundId, marketIndex, marketState) {
  if (!marketIndex || !marketState) {
    return 100;
  }
  return calcFundIndex(fundId, marketIndex, marketState);
}

/**
 * ファンドの基準価格からの変動率を取得（%）
 *
 * 新シグネチャ: getFundPriceChangeFromBase(fundId, marketIndex, marketState)
 * 旧シグネチャ（後方互換）: getFundPriceChangeFromBase(investmentData, fundId)
 *
 * @param {string|Object} fundIdOrInvestmentData - ファンドIDまたは投資データ（後方互換）
 * @param {Object|string} marketIndexOrFundId - マーケットインデックスまたはファンドID（後方互換）
 * @param {Object} marketState - 市場状態（新シグネチャのみ）
 * @returns {number} 変動率（%）
 */
export function getFundPriceChangeFromBase(fundIdOrInvestmentData, marketIndexOrFundId, marketState) {
  // 後方互換性: 第1引数がオブジェクト（investmentData）の場合
  if (typeof fundIdOrInvestmentData === 'object' && fundIdOrInvestmentData !== null) {
    const investmentData = fundIdOrInvestmentData;
    const fundId = marketIndexOrFundId;
    // 現在価格と基準価格から計算
    const currentPrice = getFundPrice(investmentData, fundId);
    const fund = ALL_FUNDS[fundId];
    const basePrice = fund?.basePrice || INVESTMENT.BASE_PRICE;
    if (basePrice === 0) return 0;
    return ((currentPrice - basePrice) / basePrice) * 100;
  }

  // 新シグネチャ: getFundPriceChangeFromBase(fundId, marketIndex, marketState)
  const fundId = fundIdOrInvestmentData;
  const marketIndex = marketIndexOrFundId;

  if (!marketIndex || !marketState) {
    return 0;
  }
  return getFundChangeRate(fundId, marketIndex, marketState);
}

/**
 * 全ファンドの現在価格を取得
 *
 * @param {Object} marketIndex - マーケットインデックス
 * @param {Object} marketState - 市場状態
 * @returns {Object} { fundId: price }
 */
export function getAllFundPrices(marketIndex, marketState) {
  if (!marketIndex || !marketState) {
    // フォールバック: 基準価格を返す
    const prices = {};
    for (const [fundId, fund] of Object.entries(ALL_FUNDS)) {
      prices[fundId] = fund.basePrice;
    }
    return prices;
  }

  const indices = getAllFundIndices(marketIndex, marketState);
  const prices = {};
  for (const [fundId, data] of Object.entries(indices)) {
    prices[fundId] = data.price;
  }
  return prices;
}

// ========================================
// 価格履歴管理
// ========================================

/**
 * 1日（1戦）ごとの価格履歴を記録
 *
 * @param {Object} investmentData - 投資データ
 * @param {Object} marketIndex - マーケットインデックス
 * @param {Object} marketState - 市場状態
 * @returns {Object} 更新された投資データ
 */
export function recordDailyFundPrices(investmentData, marketIndex, marketState) {
  // キャッシュをクリア（日付変更を想定）
  clearCacheIfNeeded(marketState?.currentDay || 0);

  // 現在のファンド価格を取得（カード価格から計算）
  const currentPrices = getAllFundPrices(marketIndex, marketState);

  // 価格履歴を更新
  const newPriceHistory = { ...investmentData.priceHistory };
  for (const [fundId, price] of Object.entries(currentPrices)) {
    if (!newPriceHistory[fundId]) {
      newPriceHistory[fundId] = [];
    }
    newPriceHistory[fundId].push(price);
    // 最新30件に制限
    if (newPriceHistory[fundId].length > 30) {
      newPriceHistory[fundId] = newPriceHistory[fundId].slice(-30);
    }
  }

  return {
    ...investmentData,
    priceHistory: newPriceHistory,
    // fundPricesは廃止（互換性のため一時的に残す）
    fundPrices: currentPrices,
  };
}

/**
 * ファンドの前日比変動率を取得（%）
 *
 * @param {Object} investmentData - 投資データ
 * @param {string} fundId - ファンドID
 * @returns {number} 変動率（%）
 */
export function getFundPriceChange(investmentData, fundId) {
  const history = investmentData.priceHistory && investmentData.priceHistory[fundId];
  if (!history || history.length < 2) return 0;

  const currentPrice = history[history.length - 1];
  const previousPrice = history[history.length - 2];

  if (previousPrice === 0) return 0;
  return ((currentPrice - previousPrice) / previousPrice) * 100;
}

/**
 * ファンドの価格履歴を取得
 *
 * @param {Object} investmentData - 投資データ
 * @param {string} fundId - ファンドID
 * @returns {number[]} 価格履歴（最大30件）
 */
export function getFundPriceHistory(investmentData, fundId) {
  return investmentData.priceHistory?.[fundId] || [];
}

// ========================================
// 後方互換性のための関数（非推奨）
// ========================================

/**
 * @deprecated fundCalculator.getFundPrice()を使用してください
 * 全ファンドの初期価格を生成
 */
export function initializeFundPrices() {
  console.warn('initializeFundPrices() is deprecated. Fund prices are now calculated from card prices.');
  const prices = {};
  for (const [fundId, fund] of Object.entries(ALL_FUNDS)) {
    prices[fundId] = fund.basePrice;
  }
  return prices;
}

/**
 * @deprecated ファンド価格はカード価格から自動計算されます
 * 1日（1戦）ごとの価格更新処理（後方互換性用ラッパー）
 */
export function processDailyPriceUpdate(investmentData, marketState, tournamentResult = null, marketIndex = null) {
  console.warn('processDailyPriceUpdate() is deprecated. Use recordDailyFundPrices() instead.');

  // marketIndexがない場合は履歴更新をスキップ
  if (!marketIndex) {
    return investmentData;
  }

  return recordDailyFundPrices(investmentData, marketIndex, marketState);
}

// ========================================
// エクスポート
// ========================================

export default {
  // 新API
  getFundPrice,
  getFundIndex,
  getFundPriceChangeFromBase,
  getAllFundPrices,
  recordDailyFundPrices,
  getFundPriceChange,
  getFundPriceHistory,
  // 後方互換性（非推奨）
  initializeFundPrices,
  processDailyPriceUpdate,
};
